import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import { sentenceCase, wait } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsSimpleInput from 'ui-elements/form-elements/FsDefaultInput';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsFormFullWidthContainer from 'ui-elements/form-elements/FsFormFullWidthContainer';
import FsPasswordInput from 'ui-elements/form-elements/FsPasswordInput';
import FsNextArrowIcon from 'ui-elements/icons/FsNextArrow';
import FsFormGridWrapper from 'ui-elements/form-elements/FsFormGridWrapper';
import FsFormGridItem from 'ui-elements/form-elements/FsFormGridItem';
import { isWeekPasswordRe } from 'ui-elements/form-elements/FsPasswordStrengthChecker/PasswordStrengthChecker.utils';

export interface FsRegisterAccountSetupFormDataFormProps {
  firstName: string;
  lastName: string;
  companyName: string;
  password: string;
  repeatPassword: string;
}

interface FsRegisterAccountSetupFormProps
  extends FsRegisterAccountSetupFormDataFormProps {
  onClose?: () => void;
  onGoNext?: (props: FsRegisterAccountSetupFormDataFormProps) => void;
}

const FsRegisterAccountSetupForm: React.FC<FsRegisterAccountSetupFormProps> = ({
  onClose,
  onGoNext,
  ...defaults
}) => {
  const formEl = useRef<HTMLFormElement>(null);
  const [firstName, setFirstName] = useState<string>(defaults.firstName);
  const [lastName, setLastName] = useState<string>(defaults.lastName);
  const [companyName, setCompanyName] = useState<string>(defaults.companyName);
  const [password, setPassword] = useState<string>(defaults.password);
  const [repeatPassword, setRepeatPassword] = useState<string>(
    defaults.repeatPassword
  );
  const [pending, setPending] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const fields = [
    {
      required: true,
      name: defaultLocales.firstName.toLowerCase(),
      value: firstName,
      label: defaultLocales.firstName,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setFirstName(e.target?.value);
      },
      autoFocus: true,
    },
    {
      name: defaultLocales.lastName.toLowerCase(),
      value: lastName,
      label: defaultLocales.lastName,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setLastName(e.target?.value);
      },
    },
    {
      required: true,
      name: defaultLocales.password.toLowerCase(),
      type: 'password',
      value: password,
      showStrengthChecker: true,
      showInfo: true,
      label: defaultLocales.createPassword,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setPassword(e.target?.value);
      },
    },
    {
      required: true,
      name: 'repeat-password',
      type: 'password',
      value: repeatPassword,
      label: defaultLocales.repeatPassword,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setRepeatPassword(e.target?.value);
      },
    },
    {
      required: true,
      name: defaults.companyName.toLowerCase(),
      value: companyName,
      label: defaultLocales.companyName,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setCompanyName(e.target?.value);
      },
    },
  ];

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPending(true);
    // eslint-disable-next-line no-console
    console.log('Save changes', {
      firstName,
      lastName,
      companyName,
      password,
      repeatPassword,
    });
    await wait(1000);
    setPending(false);
    if (onClose) onClose();
    if (onGoNext)
      onGoNext({
        firstName,
        lastName,
        companyName,
        password,
        repeatPassword,
      });
  };

  useEffect(() => {
    const isDefaultFormValid = !!formEl?.current?.checkValidity();
    const isPasswordValid =
      !!password.match(isWeekPasswordRe) && password === repeatPassword;
    const isCompanyNameValid = companyName.length >= 3;
    const isNameValid = firstName.length >= 3;
    const isValid =
      isDefaultFormValid &&
      isNameValid &&
      isPasswordValid &&
      isCompanyNameValid;
    setIsFormValid(isValid);
  }, [firstName, lastName, companyName, password, repeatPassword]);

  const [firstNameField, lastNameField, ...otherFields] = fields;

  return (
    <Box>
      <form onSubmit={handleSubmit} ref={formEl}>
        <FsFormGridWrapper>
          {[firstNameField, lastNameField].map(({ name, ...otherProps }) => (
            <FsFormGridItem key={name}>
              <FsSimpleInput
                {...{ ...otherProps, name }}
                fsVariant="outlined"
                fullWidth
              />
            </FsFormGridItem>
          ))}
        </FsFormGridWrapper>
        {otherFields.map(({ name, ...otherProps }) => (
          <FsFormFullWidthContainer key={name}>
            {name.includes('password') ? (
              <>
                <FsPasswordInput
                  {...otherProps}
                  placeholder={`${sentenceCase(name)} *`}
                  fsVariant="outlined"
                  fullWidth
                />
              </>
            ) : (
              <FsSimpleInput
                {...{ ...otherProps, name }}
                fsVariant="outlined"
                fullWidth
              />
            )}
          </FsFormFullWidthContainer>
        ))}
        <FsPendingButton
          type="submit"
          variant="contained"
          color="primary"
          pending={pending}
          disabled={!isFormValid}
          endIcon={<FsNextArrowIcon />}
          fullWidth
          spaceBetween
        >
          {defaultLocales.continue}
        </FsPendingButton>
      </form>
    </Box>
  );
};

export default FsRegisterAccountSetupForm;
